import * as React from 'react'
import { AppNotification, GlobalContext } from '../../globalContext'
import { Modal, ReservationState } from './useReservationState'

interface State<T> {
  readonly value: T
  readonly actionState: ActionState
}

export enum Mode {
  Editing,
  Requesting,
  Succeeded,
  Failed,
}

type ActionState =
  | { mode: Mode.Editing }
  | { mode: Mode.Requesting }
  | { mode: Mode.Succeeded; message: string }
  | { mode: Mode.Failed; message: string }

type Action<T> = (value: T) => Promise<void>

interface Messages {
  readonly valueEmpty?: string
  readonly succeeded: string
  readonly failed: string
}

type NotificationActionAtSucceeded = AppNotification['action']

export interface EditAttributeState<T> extends State<T> {
  readonly setState: React.Dispatch<React.SetStateAction<State<T>>>
  readonly onClose: () => void
  readonly onChange: React.ChangeEventHandler<any>
  readonly onAction: (
    action: Action<T>,
    messages: Messages,
    actionAtSucceeded?: NotificationActionAtSucceeded,
  ) => () => Promise<void>
}

export function useEditAttributeState<T>(
  pageState: ReservationState,
  notificationId: string,
  initialValue: T,
): EditAttributeState<T> {
  const [state, setState] = React.useState<State<T>>({ value: initialValue, actionState: { mode: Mode.Editing } })
  const { notifications, updateContext } = React.useContext(GlobalContext)
  const onClose = () =>
    pageState.setState({
      modal: Modal.None,
      selectedTime: '',
      reservedSpot: null,
      cancelReservation: null,
      spots: [],
      isMobile: false,
    })
  const onChange = (event: React.ChangeEvent<any>) => setState({ ...state, value: event.target.value })
  const onAction = (
    action: Action<T>,
    messages: Messages,
    actionAtSucceeded?: NotificationActionAtSucceeded,
  ) => async () => {
    if (typeof state.value === 'string' && state.value.length === 0 && messages.valueEmpty) {
      setState({ ...state, actionState: { mode: Mode.Failed, message: messages.valueEmpty } })
      return
    }
    const newNotifications = notifications.filter(n => n.id !== notificationId)
    setState({ ...state, actionState: { mode: Mode.Requesting } })
    updateContext(draft => {
      draft.notifications = [...newNotifications]
    })
    try {
      await action(state.value)
      updateContext(draft => {
        draft.notifications = [
          { id: notificationId, text: messages.succeeded, action: actionAtSucceeded },
          ...newNotifications,
        ]
      })
      setState({ ...state, actionState: { mode: Mode.Succeeded, message: messages.succeeded } })
      onClose()
    } catch (err) {
      setState({ ...state, actionState: { mode: Mode.Failed, message: messages.failed } })
    }
  }
  return {
    ...state,
    setState,
    onClose,
    onChange,
    onAction,
  }
}
